import React, { useRef, useEffect } from 'react';
import { generatePathSteps } from '../utils/stepGeneration';
import '../../../styles/modes/autoMode.css';

// Algorithm step visualization component
const StepVisualizer = ({ 
  steps, 
  currentStepIndex, 
  onStepChange, 
  locations, 
  onPathVisualizationChange 
}) => {
  // Create algorithm steps based on path segments
  const allSteps = React.useMemo(() => {
    const generatedSteps = steps.path ? generatePathSteps(steps, locations) : [];
    
    console.log("[PATH-DEBUG-STEP] Generated steps:", {
      stepsCount: generatedSteps.length,
      stepsWithPathIds: generatedSteps.map(step => ({
        step: step.step,
        pathIdsLength: step.pathIds ? step.pathIds.length : 0,
        pathIds: step.pathIds
      }))
    });
    
    return generatedSteps;
  }, [steps, locations]);
  
  // Get current step or first step if none selected
  const currentStep = allSteps[currentStepIndex] || allSteps[0];
  
  // Keep track of last sent path to avoid unnecessary updates
  const lastSentPathRef = useRef(null);

  // Update visualization path when step changes
  useEffect(() => {
    if (!currentStep || !currentStep.pathIds || !onPathVisualizationChange) {
      return;
    }
    
    // Compare with last sent path to avoid redundant updates
    const currentPathIds = currentStep.pathIds;
    const lastPathIds = lastSentPathRef.current;
    
    if (!lastPathIds || 
        lastPathIds.length !== currentPathIds.length || 
        JSON.stringify(lastPathIds) !== JSON.stringify(currentPathIds)) {

      // Update last sent path reference
      lastSentPathRef.current = [...currentPathIds];
      
      // Send path update to parent
      onPathVisualizationChange(currentPathIds);
    }
  }, [currentStep, currentStepIndex, onPathVisualizationChange]);

  // Navigate to previous step
  const handlePrevStep = () => {
    if (currentStepIndex > 0) {
      onStepChange(currentStepIndex - 1);
    }
  };

  // Navigate to next step
  const handleNextStep = () => {
    if (currentStepIndex < allSteps.length - 1) {
      onStepChange(currentStepIndex + 1);
    }
  };

  if (!currentStep || allSteps.length === 0) {
    return null;
  }

  // Filter out visited points from distances list, only keep unvisited and next point
  const filteredDistancesList = currentStep.distancesList.filter(item => !item.visited || item.isNext);

  return (
    <div className="algorithm-step-visualizer">
      <div className="step-navigation">
        <span className="step-counter">Step {currentStepIndex + 1} of {allSteps.length}</span>
        <div className="step-controls">
          <button 
            className="step-button prev"
            onClick={handlePrevStep}
            disabled={currentStepIndex === 0}
          >
            Previous
          </button>
          <button 
            className="step-button next"
            onClick={handleNextStep}
            disabled={currentStepIndex === allSteps.length - 1}
          >
            Next
          </button>
        </div>
      </div>

      <div className="step-content">
        <div className="step-description">
          <h6>Current Step:</h6>
          <p>{currentStep.description}</p>
        </div>

        <div className="step-details">
          {filteredDistancesList && filteredDistancesList.length > 0 && (
            <div className="step-distances-list">
              <h6>Distances from Current Point:</h6>
              <table className="distances-table">
                <thead>
                  <tr>
                    <th>Node</th>
                    <th>Distance (km)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDistancesList.map((item, index) => (
                    <tr 
                      key={`dist-${index}`} 
                      className={item.isNext ? 'next-point' : ''}
                    >
                      <td>{item.pointName}</td>
                      <td>{item.distance.toFixed(2)}</td>
                      <td>
                        {item.isNext ? <span className="next-label">Next Point</span> : <span className="unvisited-label">Unvisited</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="step-path-status">
            <h6>Current Path:</h6>
            <div className="current-path-sequence">
              {currentStep.currentPath.map((pointName, index) => (
                <React.Fragment key={`path-${index}`}>
                  <span className={`path-point ${index === currentStep.currentPathIndex ? 'current' : ''}`}>
                    {pointName}
                  </span>
                  {index < currentStep.currentPath.length - 1 && (
                    <span className="path-arrow">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <p className="total-distance-so-far">
              Total distance so far: <strong>{currentStep.totalDistanceSoFar.toFixed(2)} km</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepVisualizer; 